/**
 * CU-14zefhy2ydj U5: "Branch from here" and "Edit and resend". A branch is a new chat (J-3, `POST /api/assistant/chat/fork`) that
 * starts with the transcript of the old one up to a message; the old chat stays as it was. Editing a question forks from the
 * message before it and sends the new text in the branch. Pure helpers (no Angular), so they run under node in the spec.
 */

import {HistorySession, toTranscript} from 'app/layout/components/hauliermagic-assistant-chat/hauliermagic-assistant-history';

/** The body of `POST /api/assistant/chat/fork` (J-3). */
export interface ForkRequest {
    session: string;
    fromMessageIndex: number;
}

/** What the fork answers (J-3). */
export interface ForkResult {
    session: string;
    label?: string;
}

interface MessageLike {
    role: string;
    pending?: boolean;
    /** a local note or a screen-request card: not part of the conversation, never a branch point */
    note?: boolean;
    screen?: unknown;
}

/** The chat as far as a branch cares: its key and messages, plus what `toTranscript` reads to size the stored copy. */
export type BranchChat = Pick<HistorySession, 'key' | 'messages'> & Partial<HistorySession>;

/**
 * The live messages that the stored transcript keeps: finished ones only, the newest 200, the oldest dropped until the copy
 * fits 1 MB (the same rule as `toTranscript`, which stores a deep copy, so the live objects are picked by position).
 */
export function storedMessages(chat: BranchChat): any[] {
    const finished = (chat.messages || []).filter((m) => m && !m.pending);
    const kept = toTranscript({...chat, key: chat.key || '', label: chat.label || ''} as HistorySession).messages.length;
    return finished.slice(finished.length - kept);
}

/**
 * Where a message sits in the stored transcript (what the server counts): the same list `toTranscript` stores (`storedMessages`:
 * finished messages, the last 200, oldest dropped for size). -1 when the message is not stored (pending, or cut off the front).
 */
export function transcriptIndex(chat: BranchChat, message: MessageLike): number {
    return storedMessages(chat).indexOf(message);
}

/** A message can start a branch when it is a finished question or answer that is stored. */
export function canBranchFrom(chat: BranchChat, message: MessageLike): boolean {
    return (
        !!message &&
        (message.role === 'user' || message.role === 'assistant') &&
        !message.pending &&
        !message.note &&
        !message.screen &&
        transcriptIndex(chat, message) >= 0
    );
}

/** The fork request for "Branch from here" on `message`, or null when it cannot start a branch. */
export function forkRequestFrom(chat: BranchChat, message: MessageLike): ForkRequest | null {
    if (!chat || !chat.key || !canBranchFrom(chat, message)) {
        return null;
    }
    return {session: chat.key, fromMessageIndex: transcriptIndex(chat, message)};
}

/**
 * "Edit and resend" on a question: the branch keeps everything before it. null when the question is not a stored user message;
 * `fork: null` with `ok` = the question is the first message, so the edited text simply starts a new chat.
 */
export function editResendPlan(chat: BranchChat, question: MessageLike): {ok: boolean; fork: ForkRequest | null} {
    if (!chat || !chat.key || !question || question.role !== 'user' || question.pending) {
        return {ok: false, fork: null};
    }
    const index = transcriptIndex(chat, question);
    if (index < 0) {
        return {ok: false, fork: null};
    }
    return {ok: true, fork: index === 0 ? null : {session: chat.key, fromMessageIndex: index - 1}};
}

/** A chat of the server list (J-1) as far as branches care. */
export interface BranchSession {
    key: string;
    forkedFrom?: string | null;
}

/**
 * The chats of one branch family (the root chat and every chat forked from it, directly or not), root first, then in list order;
 * [] when the chat has no branch relation. Used to switch between branches of a question.
 */
export function branchFamily(sessions: BranchSession[], key: string): string[] {
    const byKey = new Map<string, BranchSession>();
    (sessions || []).forEach((s) => s && s.key && byKey.set(s.key, s));
    if (!byKey.has(key)) {
        return [];
    }
    const rootOf = (k: string): string => {
        const seen = new Set<string>();
        let current = k;
        while (!seen.has(current)) {
            seen.add(current);
            const parent = byKey.get(current)?.forkedFrom;
            if (!parent || !byKey.has(parent)) {
                return current;
            }
            current = parent;
        }
        return current;
    };
    const root = rootOf(key);
    const family = [...byKey.keys()].filter((k) => rootOf(k) === root);
    return family.length > 1 ? [root, ...family.filter((k) => k !== root)] : [];
}

/** "Branch 2 of 3" for the chat at `key` within its family; '' when it has no branches. */
export function branchPosition(family: string[], key: string): string {
    const at = family.indexOf(key);
    return at < 0 || family.length < 2 ? '' : `Branch ${at + 1} of ${family.length}`;
}
